import { useQuery } from '@tanstack/react-query'
import getCarbonInstances from '../../services/ruutsApi/getCarbonInstances'
import { CarbonInstance, ToGeoJSON } from '../../types/apiModels'
import { COLORS } from '../../utils/constants'
import { queryKeys } from './queryKeys'

export type CarbonInstanceMonitoringPeriodGroup = {
  programMonitoringPeriodId: string
  programMonitoringPeriodName: string
  carbonInstances: ToGeoJSON[]
}

// Kept as a module-level reference so react-query's `select` memoization only
// recomputes the grouping when the underlying carbon-instances data changes.
const groupByMonitoringPeriod = (
  carbonInstances: CarbonInstance[],
): CarbonInstanceMonitoringPeriodGroup[] => {
  const groupMap = new Map<string, CarbonInstanceMonitoringPeriodGroup>()

  carbonInstances.forEach(ci => {
    const mps = ci.programMonitoringPeriods ?? []
    if (mps.length === 0) return
    const geojson = ci.toGeoJSON
    if (!geojson) return

    // Guarantee a non-null color so all consumers (DrawingLayers, ExclusionAreaMap)
    // receive a stable value without needing their own fallback logic.
    const normalizedGeojson = {
      ...geojson,
      properties: {
        ...geojson.properties,
        color: geojson.properties?.color ?? COLORS.CARBON_INSTANCE,
      },
    }

    mps.forEach(mp => {
      const existing = groupMap.get(mp.id)
      if (existing) {
        existing.carbonInstances.push(normalizedGeojson)
      } else {
        groupMap.set(mp.id, {
          programMonitoringPeriodId: mp.id,
          programMonitoringPeriodName: mp.name,
          carbonInstances: [normalizedGeojson],
        })
      }
    })
  })

  return Array.from(groupMap.values()).sort((a, b) =>
    a.programMonitoringPeriodName.localeCompare(b.programMonitoringPeriodName, undefined, {
      numeric: true,
    }),
  )
}

export const useCarbonInstancesGroupedByMonitoringPeriod = ({
  farmId,
}: {
  farmId: string | undefined
}) => {
  const result = useQuery({
    queryKey: queryKeys.carbonInstances(farmId),
    queryFn: ({ signal }) => getCarbonInstances({ farmId: farmId as string, signal }),
    enabled: !!farmId,
    select: groupByMonitoringPeriod,
  })

  return {
    groups: result.data ?? [],
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
  }
}
